/**
 * d-freeship-by-state.js
 *
 * Recalcula o threshold de frete grátis da barra do carrinho lateral
 * (d-side-cart) de acordo com o estado do cliente, resolvido nesta ordem:
 *   1. localStorage 'disco_cep' (CEP informado no announcement bar ou no
 *      cálculo de frete do carrinho — ambos gravam o mesmo formato)
 *   2. localStorage 'd_geo_state' (cache de geolocalização, TTL de 7 dias)
 *   3. Geolocalização do navegador + reverse geocode via BigDataCloud
 *      (sem API key), silenciosa
 * Sem estado resolvido ou sem regra correspondente, usa o valor padrão
 * configurado na section (data-fallback-threshold).
 */
(function () {
  'use strict';

  var GEO_CACHE_KEY = 'd_geo_state';
  var GEO_TTL_MS = 7 * 24 * 60 * 60 * 1000;

  function loadRules(root) {
    var el = root.querySelector('[data-freeship-rules]');
    if (!el) return [];
    try {
      var rules = JSON.parse(el.textContent);
      return Array.isArray(rules) ? rules : [];
    } catch (err) {
      console.error('[d-freeship-by-state] regras inválidas', err);
      return [];
    }
  }

  function stateFromDiscoCep() {
    try {
      var raw = localStorage.getItem('disco_cep');
      if (!raw) return null;
      var data = JSON.parse(raw);
      return data && data.uf ? String(data.uf).toUpperCase() : null;
    } catch (err) {
      return null;
    }
  }

  function stateFromGeoCache() {
    try {
      var raw = localStorage.getItem(GEO_CACHE_KEY);
      if (!raw) return null;
      var data = JSON.parse(raw);
      if (!data || !data.uf || !data.ts) return null;
      if (Date.now() - data.ts > GEO_TTL_MS) return null;
      return String(data.uf).toUpperCase();
    } catch (err) {
      return null;
    }
  }

  function cacheGeoState(uf) {
    try {
      localStorage.setItem(GEO_CACHE_KEY, JSON.stringify({ uf: uf, ts: Date.now() }));
    } catch (err) {}
  }

  function resolveStateByGeolocation() {
    return new Promise(function (resolve) {
      if (!navigator.geolocation) {
        resolve(null);
        return;
      }
      navigator.geolocation.getCurrentPosition(
        function (pos) {
          var lat = pos.coords.latitude;
          var lng = pos.coords.longitude;
          fetch(
            'https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=' +
              lat +
              '&longitude=' +
              lng +
              '&localityLanguage=pt'
          )
            .then(function (r) {
              return r.json();
            })
            .then(function (data) {
              var code = data && data.principalSubdivisionCode;
              var uf = code && code.indexOf('BR-') === 0 ? code.slice(3).toUpperCase() : null;
              if (uf) cacheGeoState(uf);
              resolve(uf);
            })
            .catch(function () {
              resolve(null);
            });
        },
        function () {
          resolve(null);
        },
        { timeout: 8000, maximumAge: GEO_TTL_MS }
      );
    });
  }

  function resolveState() {
    var fromCep = stateFromDiscoCep();
    if (fromCep) return Promise.resolve(fromCep);
    var fromGeoCache = stateFromGeoCache();
    if (fromGeoCache) return Promise.resolve(fromGeoCache);
    return resolveStateByGeolocation();
  }

  function findThreshold(rules, uf, fallbackCents) {
    if (uf) {
      for (var i = 0; i < rules.length; i++) {
        var states = rules[i].states || [];
        for (var j = 0; j < states.length; j++) {
          if (String(states[j]).toUpperCase() === uf) return rules[i].threshold_cents;
        }
      }
    }
    return fallbackCents;
  }

  function formatMoney(cents) {
    var value = cents / 100;
    try {
      return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    } catch (err) {
      return 'R$ ' + value.toFixed(2).replace('.', ',');
    }
  }

  function getCartSubtotal() {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        return cart.items_subtotal_price || 0;
      })
      .catch(function () {
        return 0;
      });
  }

  function render(root, thresholdCents, subtotalCents) {
    var msgEl = root.querySelector('[data-freeship-msg]');
    var barEl = root.querySelector('.d-sct__freeship-bar-fill');
    var trackEl = root.querySelector('.d-sct__freeship-bar-track');
    if (!msgEl || !barEl) return;

    var msgRemainingTpl = root.dataset.msgRemaining || 'Faltam {amount} para o frete grátis';
    var msgAchieved = root.dataset.msgAchieved || '🎉 Você ganhou frete grátis!';

    var progress = thresholdCents > 0 ? Math.min(100, Math.floor((subtotalCents * 100) / thresholdCents)) : 100;

    if (progress >= 100) {
      msgEl.innerHTML = '<strong>' + msgAchieved + '</strong>';
    } else {
      var remaining = Math.max(0, thresholdCents - subtotalCents);
      msgEl.textContent = msgRemainingTpl.replace('{amount}', formatMoney(remaining));
    }

    barEl.style.width = progress + '%';
    var complete = progress >= 100;
    barEl.style.background = complete ? barEl.dataset.colorComplete || '#16a34a' : barEl.dataset.color || '#3b82f6';
    barEl.dataset.width = progress;
    barEl.dataset.complete = progress;
    if (trackEl) trackEl.setAttribute('aria-valuenow', progress);
  }

  function runForRoot(root) {
    var rules = loadRules(root);
    var fallbackCents = parseInt(root.dataset.fallbackThreshold, 10) || 0;

    Promise.all([resolveState(), getCartSubtotal()]).then(function (results) {
      var uf = results[0];
      var subtotalCents = results[1];
      var thresholdCents = findThreshold(rules, uf, fallbackCents);
      render(root, thresholdCents, subtotalCents);
    });
  }

  function run() {
    document.querySelectorAll('[data-freeship-root]').forEach(runForRoot);
  }

  document.addEventListener('DOMContentLoaded', run);
  window.addEventListener('cart-drawer-open', run);
  document.addEventListener('cart-change', run);
  document.addEventListener('d-location-change', run);

  window.dFreeshipRecalc = run;
})();
